import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Cat, Dog, PawPrint, Pencil, Plus } from "lucide-react";
import { Card } from "@shared/ui/containers/Card";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { EmptyState } from "@shared/ui/feedback/EmptyState";
import { Button } from "@shared/ui/primitives/Button";
import { Fab } from "@shared/ui/primitives/Fab";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { pageCopy } from "@shared/lib/pageCopy";
import { fetchProfile } from "@features/profile/api";
import { fetchMemberPets, type MemberPet } from "./api";

export function Pets(): JSX.Element {
  const navigate = useNavigate();
  const copy = pageCopy().pets;

  const profileQuery = useQuery({
    queryKey: ["profile"],
    queryFn: fetchProfile,
  });
  const memberId = profileQuery.data?.member_id ?? profileQuery.data?.memberId;
  const petsQuery = useQuery({
    queryKey: ["pets", memberId],
    queryFn: () => fetchMemberPets(memberId!),
    enabled: !!memberId,
  });

  const header = (
    <HeroHeader
      eyebrow={copy.eyebrow}
      title={copy.title}
      subtitle={copy.subtitle}
    />
  );

  if (profileQuery.isLoading || petsQuery.isLoading) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="flex flex-col gap-3 px-5 pt-5">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      </div>
    );
  }

  if (profileQuery.isError || petsQuery.isError) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5">
          <ErrorState
            onRetry={() => {
              profileQuery.refetch();
              petsQuery.refetch();
            }}
          />
        </div>
      </div>
    );
  }

  if (!memberId) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5">
          <ErrorState
            title="No member profile found"
            description="Pets are linked to your member profile. Please contact estate management if this keeps happening."
          />
        </div>
      </div>
    );
  }

  const pets = petsQuery.data ?? [];

  return (
    <div className="min-h-full bg-paper">
      {header}

      <div className="px-5 pt-5 pb-[calc(8rem+env(safe-area-inset-bottom,0px))]">
        {pets.length === 0 ? (
          <EmptyState
            icon={<PawPrint className="h-7 w-7" strokeWidth={1.5} />}
            title="No pets registered."
            description="Add your pets so estate records stay up to date."
            action={
              <Button onClick={() => navigate("/app/pets/new")}>Add pet</Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-3">
            {pets.map((pet) => (
              <Card key={pet.pet_id} noGrain className="p-4">
                <div className="flex gap-3">
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-sunken hairline">
                    {pet.photo_url ? (
                      <img
                        src={pet.photo_url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-ink-muted">
                        {petIcon(pet)}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-display text-headline text-ink truncate">
                          {pet.name}
                        </p>
                        <span className="mt-1 inline-flex rounded-pill bg-primary-100 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-primary-700">
                          {pet.pet_type_name ?? "Pet"}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => navigate(`/app/pets/${pet.pet_id}/edit`)}
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                        Edit
                      </Button>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-caption text-ink-muted">
                      <PetDetail label="Breed" value={pet.breed} />
                      <PetDetail label="Colour" value={pet.colour} />
                      <PetDetail label="Gender" value={pet.gender} />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Fab
        draggable
        label="Add a pet"
        onClick={() => navigate("/app/pets/new")}
      >
        <Plus className="h-5 w-5" strokeWidth={2.2} />
      </Fab>
    </div>
  );
}

function PetDetail({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}): JSX.Element {
  return (
    <span>
      <span className="block uppercase tracking-[0.12em]">{label}</span>
      <span className="mt-0.5 block text-body-sm text-ink-secondary">
        {value?.trim() || "-"}
      </span>
    </span>
  );
}

function petIcon(pet: MemberPet): JSX.Element {
  const type = (pet.pet_type_name ?? "").toLowerCase();
  if (type.includes("cat"))
    return <Cat className="h-8 w-8" strokeWidth={1.4} />;
  if (type.includes("dog"))
    return <Dog className="h-8 w-8" strokeWidth={1.4} />;
  return <PawPrint className="h-8 w-8" strokeWidth={1.4} />;
}
